import type {
  UserGuideBlock,
  UserGuideTableCell,
} from "../../entities/user-guide";

/**
 * Прежняя ссылка на страницу в Confluence. Остаётся запасным путём: пока
 * интеграция не настроена или копия не собралась, человек всё равно должен
 * добраться до инструкции.
 */
export const GUIDE_FALLBACK_URL =
  "https://pixelprime.atlassian.net/wiki/spaces/~71202093e617048d3148baad8ec58e20799a5e/pages/31555585";

export const userGuideKey = ["user-guide"] as const;

/**
 * Ссылка из текста инструкции. Ядро уже отбросило опасные адреса, но экран не
 * полагается на это: `javascript:` и подобное здесь не пройдёт ни при каких
 * обстоятельствах.
 */
export type GuideLink =
  | { readonly kind: "anchor"; readonly id: string }
  | { readonly kind: "external"; readonly href: string }
  | { readonly kind: "contact"; readonly href: string };

export function classifyLink(href: string | null): GuideLink | null {
  if (href === null) return null;
  const value = href.trim();
  if (value.startsWith("#") && value.length > 1) {
    let id = value.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch {
      // Битая процентная запись остаётся как есть: якорь просто не найдётся.
    }
    return { kind: "anchor", id };
  }
  try {
    const url = new URL(value);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return { kind: "external", href: url.href };
    }
    if (url.protocol === "mailto:" || url.protocol === "tel:") {
      return { kind: "contact", href: value };
    }
  } catch {
    // Не адрес — значит, и не ссылка.
  }
  return null;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function plural(count: number, one: string, few: string, many: string): string {
  const tens = count % 100;
  const units = count % 10;
  if (tens >= 11 && tens <= 14) return many;
  if (units === 1) return one;
  if (units >= 2 && units <= 4) return few;
  return many;
}

const moment = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** «27 сентября 2026 г., 14:05» — время правки в Confluence. */
export function formatMoment(iso: string | null): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : moment.format(date);
}

/**
 * «3 минуты назад» — насколько свежа проверка. Точность до минуты: секунды
 * здесь только беспокоят, а экран всё равно обновляется раз в минуту.
 */
export function formatAgo(iso: string | null, now: number): string | null {
  if (iso === null) return null;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return null;
  const elapsed = Math.max(0, now - time);
  if (elapsed < MINUTE) return "только что";
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${minutes} ${plural(minutes, "минуту", "минуты", "минут")} назад`;
  }
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return `${hours} ${plural(hours, "час", "часа", "часов")} назад`;
  }
  const days = Math.floor(elapsed / DAY);
  if (days === 1) return "вчера";
  if (days < 7) return `${days} ${plural(days, "день", "дня", "дней")} назад`;
  return formatMoment(iso);
}

export interface GuideImageRef {
  readonly imageId: string;
  readonly alt: string;
  readonly caption: string | null;
}

/**
 * Все картинки инструкции в порядке чтения. По ним листает окно просмотра:
 * стрелками можно пройти скриншоты подряд, не закрывая его.
 */
export function collectImages(
  blocks: readonly UserGuideBlock[],
): GuideImageRef[] {
  const images: GuideImageRef[] = [];
  const visit = (items: readonly UserGuideBlock[]) => {
    for (const block of items) {
      switch (block.type) {
        case "image":
          if (block.image_id !== null) {
            images.push({
              imageId: block.image_id,
              alt: block.alt,
              caption: block.caption,
            });
          }
          break;
        case "list":
          block.items.forEach(visit);
          break;
        case "table":
          block.rows.forEach((row) =>
            row.forEach((cell) => visit(cell.blocks)),
          );
          break;
        case "quote":
        case "callout":
        case "expand":
          visit(block.blocks);
          break;
        default:
          break;
      }
    }
  };
  visit(blocks);
  return images;
}

/**
 * Таблицу можно разложить карточками на узком экране, только если у неё есть
 * строка заголовков и нет объединённых ячеек: иначе не ясно, чья подпись у
 * какой ячейки, и остаётся честная горизонтальная прокрутка.
 */
export function stackableHeaders(
  rows: readonly (readonly UserGuideTableCell[])[],
): string[] | null {
  const [head, ...body] = rows;
  if (head === undefined || body.length === 0) return null;
  if (!head.every((cell) => cell.header)) return null;
  const merged = rows.some((row) =>
    row.some((cell) => cell.colspan > 1 || cell.rowspan > 1),
  );
  if (merged || body.some((row) => row.length !== head.length)) return null;
  return head.map((cell) => blocksText(cell.blocks));
}

/** Простой текст блоков: подписи столбцов и доступные имена. */
export function blocksText(blocks: readonly UserGuideBlock[]): string {
  const parts: string[] = [];
  for (const block of blocks) {
    if (block.type === "paragraph") {
      parts.push(
        block.inlines
          .map((inline) => (inline.type === "break" ? " " : inline.text))
          .join(""),
      );
    } else if (block.type === "heading" || block.type === "code") {
      parts.push(block.text);
    }
  }
  return parts.join(" ").trim();
}
